import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { uniq, difference, compact } from 'lodash';
import { forkJoin, of, Observable, ReplaySubject } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { AnalyticsApiService, BackendService, MessageService, ObjectBaseService, IQueryFilterWrapper, Query, Match, QueryFilter, RequestOptionsArgs, IGlobalFiltersWrapper, PublicLinkPasswordService, cloneDeepPreservingQueries } from '@hub-components';
import { DataSlicersService } from './data-slicers.service';
import { UPLOAD_IMAGE, INSERT_URL } from '../constants';
import { Dashboard, DashboardJson, Page } from '../models/dashboard.model';
import { DashboardsResponse } from '../models/dashboards-response.model';
import { DataSliceEditor, DataSliceViewer } from '../models/data-slicer.model';
import { DashboardWidget } from '../modules/dashboards/models/dashboard-widget.model';
import { WIDGETS_BY_NAME } from '../modules/widgets/widgets.module';
import { Audit } from 'app/apps/dm/app/models/audit.model';

@Injectable()
export class DashboardsService extends ObjectBaseService {
  schemas: { [key: string]: any } = {};
  localDashboard: Dashboard;
  localLastSavedDashboard: Dashboard;
  globalFiltersData: IGlobalFiltersWrapper;
  public currentAccountId: string;
  private widgetExportingStatus$ = new ReplaySubject<{ status: string, channelName: string }>();

  constructor(
    private backendService: BackendService,
    private analyticsApi: AnalyticsApiService,
    private messageService: MessageService,
    private dataSlicersService: DataSlicersService,
    private publicLinkPasswordService: PublicLinkPasswordService,
  ) {
    super();
  }

  list(options: RequestOptionsArgs = {}): Observable<DashboardsResponse> {
    return this.backendService
      .load('dashboards', options)
      .pipe(
        map((response: { dashboards: DashboardJson[], dashboards_count: number }) => {
          return {
            dashboards: response.dashboards.map(dashboard => new Dashboard(dashboard)),
            dashboardsCount: response.dashboards_count,
          };
        }),
      );
  }

  listMyDashboards(options: RequestOptionsArgs = {}): Observable<DashboardsResponse> {
    return this.backendService.load('dashboards/my_dashboards', options).pipe(
      map((response: { dashboards: DashboardJson[], dashboards_count: number }) => {
        return {
          dashboards: response.dashboards.map(dashboard => new Dashboard(dashboard)),
          dashboardsCount: response.dashboards_count,
        };
      }),
    );
  }

  listScheduledDashboards(options: RequestOptionsArgs = {}): Observable<DashboardsResponse> {
    return this.backendService.load('dashboards/scheduled_dashboards', options).pipe(
      map((response: { dashboards: DashboardJson[], dashboards_count: number }) => {
        return {
          dashboards: response.dashboards.map(dashboard => new Dashboard(dashboard)),
          dashboardsCount: response.dashboards_count,
        };
      }),
    );
  }

  listSharedDashboards(options: RequestOptionsArgs = {}): Observable<DashboardsResponse> {
    return this.backendService.load('dashboards/shared_dashboards', options).pipe(
      map((response: { dashboards: DashboardJson[], dashboards_count: number }) => {
        return {
          dashboards: response.dashboards.map(dashboard => new Dashboard(dashboard)),
          dashboardsCount: response.dashboards_count,
        };
      }),
    );
  }

  load(dashbordId: string): Observable<Dashboard> {
    return this.backendService.load(`dashboards/${dashbordId}`, { loaderText: 'Loading Dashboard' }).pipe(
      mergeMap(res => this.build(res)),
    );
  }

  loadShared(shareId: number): Observable<Dashboard> {
    return this.backendService.load(`shared_dashboards/${shareId}`).pipe(
      mergeMap(res => this.build(res, shareId)),
    );
  }

  loadPublic(uuid: string): Observable<Dashboard> {
    const password = this.publicLinkPasswordService.getPassword(uuid);
    return this.backendService.create(`s/${uuid}/show`, { password }, { throwErrors: true }).pipe(
      mergeMap(res => this.build(res)),
    );
  }

  changeOwner(dashboardId, newOwner) {
    return this.backendService.update(`/dashboards/${dashboardId}/change_owner`, { owner: newOwner });
  }

  changeName(dashboardId, newName): Observable<Dashboard> {
    return this.backendService.update(`/dashboards/${dashboardId}/change_name`, { name: newName });
  }

  create(dashboard: Dashboard): Observable<Dashboard> {
    return this.backendService.create('dashboards', dashboard.asJson()).pipe(
      mergeMap((res: any) => this.build(res)),
    );
  }

  update(dashboard: Dashboard): Observable<Dashboard> {
    return this.backendService.update(`dashboards/${dashboard.id}`, dashboard.asJson()).pipe(
      mergeMap((res: any) => this.build(res)),
    );
  }

  delete(dashboard: Dashboard) {
    return this.backendService.delete(`dashboards/${dashboard.id}`);
  }

  exportDashboard(dashboard: Dashboard, interval: string, format: string, dateRange?: string) {
    return this.backendService.create(`/dashboards/${dashboard.id}/export`, { interval: interval, format: format, date_range: dateRange });
  }

  exportWidget(dashboardId: number, pageId: number, widgetIndex: number, interval: string) {
    return this.backendService.create(`/dashboards/${dashboardId}/pages/${pageId}/widgets/${widgetIndex}/export`, { interval: interval });
  }

  exportPublicWidget(uuid: string, pageId: number, widgetIndex: number, interval: string) {
    const password = this.publicLinkPasswordService.getPassword(uuid);
    return this.backendService.create(`s/${uuid}/export_widget`, { page_id: pageId, widget_index: widgetIndex, interval: interval, password });
  }

  exportPublicDashboard(uuid: string, interval: string, format: string, dateRange?: string) {
    const password = this.publicLinkPasswordService.getPassword(uuid);
    return this.backendService.create(`s/${uuid}/export`, { interval: interval, format: format, password, date_range: dateRange });
  }

  updateLocalDashboard(dashboard: Dashboard, lastSavedDashboard?: Dashboard) {
    this.localDashboard = dashboard;
    this.localLastSavedDashboard = lastSavedDashboard;
  }

  getLocalDashboard(): Dashboard {
    return this.localDashboard;
  }

  getLocalLastSavedDashboard(): Dashboard {
    return this.localLastSavedDashboard;
  }

  private defaultSettingsInterval() {
    return {
      type: 'dynamic' as 'dynamic',
      dynamic: { title: 'Last 7 Days', name: 'last', num: 7, unit: 'day' as 'day' },
    };
  }

  new(): Observable<Dashboard> {
    return of(new Dashboard({
      name: 'New Dashboard',
      settings: {
        backgroundColor: '#ffffff',
        size: {
          preset: {
            value: 'large-laptop',
            width: 1440,
            height: 900,
          },
          width: undefined,
          height: undefined,
        },
        gridSize: {
          value: 20,
        },
        interval: this.defaultSettingsInterval(),
      },
    }));
  }

  accountUsers() {
    return this.backendService.load('/account_users', { skipLoader: true, cache: true });
  }

  appUsers() {
    return this.backendService.load('/app_users', { skipLoader: true, cache: true });
  }

  appSuites() {
    return this.backendService.load('/app_suites', { skipLoader: true, cache: true });
  }

  build(res: DashboardJson, shareId = undefined): Observable<Dashboard> {
    return this.parse(res, shareId).pipe(map(dashboard => {
      if (dashboard.settings.legacyInterval) {
        dashboard.settings.interval = this.defaultSettingsInterval();
        this.messageService.info('The date format for this dashboard is no longer supported. Please save the dashboard with a new date range.');
        setTimeout(() => {
          this.messageService.hide();
        }, 6000);
      }
      if (dashboard.dataSlicers) {
        this.dataSlicersService.setDataSlicers(dashboard.dataSlicers);
      }
      return dashboard;
    }));
  }

  private parse(res: DashboardJson, shareId = undefined): Observable<Dashboard> {
    let datasets = [];
    if (res?.pages) {
      res.pages.forEach(p => {
        const pageDatasets = p.widgets
          .filter(w => !WIDGETS_BY_NAME[w.type].isBasicWidget)
          .filter(w => !(w.type === 'image' && (w.options?.imageUrl || w.options?.image?.imageSource === 'uploadOrInsertUrl' || w.options?.image?.imageSource === UPLOAD_IMAGE || w.options?.image?.imageSource === INSERT_URL)))
          .map(w => w.query?.dataset);

        datasets = uniq(compact([...datasets, ...pageDatasets]));
      });
    } else {
      datasets = uniq(compact(
        res.widgets
          .filter(w => !WIDGETS_BY_NAME[w.type].isBasicWidget)
          .filter(w => !(w.type === 'image' && (w.options?.imageUrl || w.options?.image?.imageSource === 'uploadOrInsertUrl' || w.options?.image?.imageSource === UPLOAD_IMAGE || w.options?.image?.imageSource === INSERT_URL)))
          .map(w => w.query?.dataset),
      ));
    }

    datasets = difference(datasets, Object.keys(this.schemas));
    if (datasets.length > 0 && (!this.currentAccountId || this.currentAccountId === res.account)) {
      return forkJoin(
        datasets.map(ds => {
          return this.analyticsApi.loadSchema(ds, { loaderText: 'Loading Dashboard', cache: true, params: { share_id: shareId }, throwErrors: true }).pipe(
            catchError(response => {
              this.backendService.handleError(response);
              return of(null);
            }),
          );
        }),
      ).pipe(mergeMap(schemaList => {
        for (const index in schemaList) {
          const dataset = datasets[index];
          this.schemas[dataset] = schemaList[index];
        }
        return of(new Dashboard(res, this.schemas));
      }));
    } else {
      return of(new Dashboard(res, this.schemas));
    }
  }

  updateGlobalFilterSelectionsToWidgets(page: Page, filters) {
    return page.widgets.map(widget => this.updateGlobalFilterSelectionsToWidget(widget, filters, page.id));
  }

  updateGlobalFilterSelectionsToWidget(widget: DashboardWidget, filters: any[], pageId: number) {
    const query = widget.query;
    query.globalFilters = filters.filter(gf => gf.dataset === query.dataset && (gf.pageId !== undefined ? gf.pageId === pageId : true));

    return widget;
  }

  updateGlobalFilter(dashboard: Dashboard, dataset: string, pageId:number, globalFilters: any) {
    const datasetFilters = dashboard.globalFilters.data.filter(gf => !(gf.dataset === dataset && gf.pageId === pageId));
    dashboard.globalFilters.data = [...datasetFilters, ...globalFilters];
    this.setGlobalFilters(dashboard.globalFilters);

    dashboard.pages.forEach(page => page.widgets = this.updateGlobalFilterSelectionsToWidgets(page, dashboard.globalFilters.data));
  }

  updateDataSlicersFilters(dashboard: Dashboard, slice: DataSliceViewer, selectedValues: Match[]) {
    const dataSlicersFilters = this.getMergedDataSlicersFilters(dashboard, slice, selectedValues);
    const dashboardPage = dashboard.pages.find(page => page.id === slice.page);
    dashboardPage.widgets.forEach(widget => {
      const query: Query = widget.query;
      const dataSlicersAppliedFilters = dataSlicersFilters.filter(df => df.dataset === query.dataset && (df.pageId === undefined || df.pageId === slice.page));
      const conflictingFilters = this.conflictingFilters(dataSlicersAppliedFilters, widget.query.filters);
      if (conflictingFilters.length > 0) {
        const filterNames = conflictingFilters.map(dim => dim.displayName || dim.name).sort().join(', ');
        this.messageService.warning(`Some widgets have filters on the same dimension as the data slicer (${filterNames}). This may result in unexpected behavior, such as empty widgets or missing data.`);
      }
      query.dataSlicersFilters = dataSlicersAppliedFilters;
    });
  }

  private conflictingFilters(dataSlicersFilters: IQueryFilterWrapper[], widgetFilters: IQueryFilterWrapper[]) {
    const dimsInDataSlicers = dataSlicersFilters.flatMap(df => df.expressions.map(e => e.dimensionProperty || e.dimension));
    const dimsInWidgetFilter = widgetFilters.flatMap(f => f.expressions.map(e => e.dimensionProperty?.name || e.dimension?.name));
    const dimsInBoth = dimsInDataSlicers.filter(dim => dimsInWidgetFilter.includes(dim?.name));
    return dimsInBoth;
  }

  setGlobalFilters(filters: IGlobalFiltersWrapper) {
    this.globalFiltersData = filters;
  }

  public createDashboardCopy(dashboard: Dashboard, newName: string): Dashboard {
    const dashboardCopy = cloneDeepPreservingQueries(dashboard);
    dashboardCopy.name = newName;
    dashboardCopy.publicUuid = undefined;
    dashboardCopy.dropShareSignature();
    dashboardCopy.dataSlicers = dashboard.dataSlicers?.map(slicer => new DataSliceEditor({ ...slicer, id: undefined }));
    return dashboardCopy;
  }

  get globalFiltersVersion(): number {
    return this.globalFiltersData.version;
  }

  get globalFilters(): IQueryFilterWrapper[] {
    if (this.globalFiltersData && this.globalFiltersData.data) {
      return this.globalFiltersData.data;
    }
    return [];
  }

  public get widgetExportingStatus(): Observable<{ status: string, channelName: string }> {
    return this.widgetExportingStatus$.asObservable();
  }

  public setWidgetExportingStatus(status: { status: string, channelName: string }) {
    this.widgetExportingStatus$.next(status);
  }

  private getMergedDataSlicersFilters(dashboard: Dashboard, slice: DataSliceViewer, selectedValues: Match[]): IQueryFilterWrapper[] {
    const uniqueFiltersMap = new Map<string, IQueryFilterWrapper>();
    const dashboardPageSlice = dashboard.pages.find(p => p.id === slice.page);

    dashboardPageSlice?.widgets
      .filter(widget => widget.query?.dataSlicersFilters?.length)
      .forEach(widget => {
        widget.query!.dataSlicersFilters.forEach(filter => {
          filter.expressions.forEach(expr => {

            if (filter.dataset === slice.dataset.name &&
                filter.pageId === slice.page &&
                expr.dimension.name === slice.dimension.name) {
              return;
            }

            const key = `${filter.dataset}:${filter.pageId}:${expr.dimension.name}`;
            if (!uniqueFiltersMap.has(key)) {
              uniqueFiltersMap.set(key, {
                pageId: filter.pageId,
                dataset: filter.dataset,
                expressions: [expr],
              });
            }
          });
        });
      });

    if (selectedValues.length) {
      const newExpression = new QueryFilter({
        dataset: slice.dataset.name,
        type: 'any',
        values: selectedValues,
        dimension: slice.dimension,
      });

      const key = `${slice.dataset.name}:${slice.page}:${slice.dimension.name}`;
      const queryFilterWrapper: IQueryFilterWrapper = {
        pageId: slice.page,
        dataset: slice.dataset.name,
        expressions: [newExpression],
      };
      uniqueFiltersMap.set(key, queryFilterWrapper);
    }

    return Array.from(uniqueFiltersMap.values());
  }

  public getAudits(offset: number, limit: number, dashboardId?: string): Observable<{ audits: Audit[], total: number }> {
    const params: any = { offset, limit };
    
    let url = '/dashboards/audits';
    if (dashboardId) {
      url = `/dashboards/${dashboardId}/audits`;
    }
    
    return this.backendService.load(url, { skipLoader: true, params })
      .pipe(
        map(response => (
          {
            audits: response.audits.map(audit => new Audit(audit)),
            total: response.total,
          })),
      );
  }

  public restore(id: string) {
    return this.backendService.create(`/dashboards/audits/${id}/restore`, {});
  }
}
